import React, { useEffect, useState } from 'react';
import '../styles/styles.css';
import '../styles/Account.css';

const Account = ({ account }) => {
  const [nftData,        setNftData]        = useState([]);
  const [collectionsMap, setCollectionsMap] = useState({});
  const [ethPrice,       setEthPrice]       = useState(null);
  // Start as false if a prefetch cache already exists — no loading flash on first visit
  const [loading, setLoading] = useState(() => {
    if (!account) return false;
    return !sessionStorage.getItem(`nft_cache_${account.toLowerCase()}`);
  });

  // Read floor prices from collections cache written by CollectionsTable
  useEffect(() => {
    const raw = localStorage.getItem('collections_cache_v2');
    if (raw) {
      try {
        const collections = JSON.parse(raw);
        const map = {};
        collections.forEach((c) => {
          map[c.collection] = {
            name:       c.name || c.collection,
            imageUrl:   c.image_url || '',
            floorPrice: parseFloat(c.stats?.total?.floor_price || 0),
          };
        });
        setCollectionsMap(map);
      } catch (_) {}
    }
  }, []);

  useEffect(() => {
    fetch('/api/eth-price')
      .then((r) => r.json())
      .then((d) => setEthPrice(d.usd))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!account) return;

    const cacheKey = `nft_cache_${account.toLowerCase()}`;
    const groupNfts = (nfts) => Object.values(
      nfts.reduce((acc, nft) => {
        const slug = nft.collection;
        acc[slug] = acc[slug] || { slug, count: 0 };
        acc[slug].count += 1;
        return acc;
      }, {})
    );

    // Use prefetch cache from wallet connection — no loading state needed
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        setNftData(groupNfts(JSON.parse(cached)));
        setLoading(false);
        return;
      } catch (_) {}
    }

    // No cache yet (e.g. restored from localStorage on page refresh) — fetch normally
    setLoading(true);
    fetch(`/api/nfts/${account}`)
      .then((r) => r.json())
      .then((nfts) => {
        setNftData(groupNfts(nfts));
        try { sessionStorage.setItem(cacheKey, JSON.stringify(nfts)); } catch (_) {}
      })
      .catch(() => setNftData([]))
      .finally(() => setLoading(false));
  }, [account]);

  // Pre-compute all row values, then sort highest value first
  const rows = nftData
    .map((nft) => {
      const col   = collectionsMap[nft.slug] || {};
      const floor = col.floorPrice || 0;
      const value = floor * nft.count;
      return { ...nft, col, floor, value };
    })
    .sort((a, b) => b.value - a.value);

  const totalNFTs  = rows.reduce((sum, r) => sum + r.count, 0);
  const totalETH   = rows.reduce((sum, r) => sum + r.value, 0);
  const totalUSD   = ethPrice ? totalETH * ethPrice : null;

  const fmtUSD = (eth) => {
    if (!ethPrice || !eth) return null;
    const usd = eth * ethPrice;
    if (usd >= 1e6) return `$${(usd / 1e6).toFixed(2)}M`;
    if (usd >= 1e3) return `$${(usd / 1e3).toFixed(1)}K`;
    return `$${usd.toFixed(0)}`;
  };

  return (
    <div className="container">
      <div className="page-content">
        <div className="about-hero">
          <p className="about-eyebrow">Portfolio</p>
          <h1 className="about-headline">My holdings.</h1>
        </div>
        {account ? (
          <>
            <div className="wallet-chip">{account}</div>
            <div className="account-summary">
              <div className="account-stat">
                <span>Collections</span>
                <strong>{rows.length}</strong>
              </div>
              <div className="account-stat">
                <span>NFTs Owned</span>
                <strong>{totalNFTs}</strong>
              </div>
              <div className="account-stat">
                <span>Portfolio Value</span>
                <strong>{totalETH.toFixed(3)} ETH</strong>
                {totalUSD && <em>{fmtUSD(totalETH)}</em>}
              </div>
            </div>
            {loading ? (
              <p className="loading-text">Loading…</p>
            ) : rows.length > 0 ? (
              <div className="table-container">
                <table className="portfolio-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Collection</th>
                      <th>Floor</th>
                      <th>Owned</th>
                      <th>Value</th>
                      <th className="share-col">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => {
                      const pct = totalETH > 0 ? (row.value / totalETH) * 100 : 0;
                      return (
                        <tr key={row.slug} className={idx === 0 ? 'portfolio-top' : undefined}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              {row.col.imageUrl && (
                                <img className="collection-image" src={row.col.imageUrl} alt="" />
                              )}
                              {row.col.name || row.slug}
                            </div>
                          </td>
                          <td>
                            {row.floor > 0 ? row.floor.toFixed(3) : '—'} ETH
                            {fmtUSD(row.floor) && (
                              <span className="cell-sub">{fmtUSD(row.floor)}</span>
                            )}
                          </td>
                          <td>{row.count}</td>
                          <td className="value-col">
                            <span className="value-eth">
                              {row.value > 0 ? row.value.toFixed(3) : '—'} ETH
                            </span>
                            {fmtUSD(row.value) && (
                              <span className="cell-sub">{fmtUSD(row.value)}</span>
                            )}
                          </td>
                          <td className="share-col">
                            <span className="share-pct">{pct.toFixed(1)}%</span>
                            <div className="share-bar">
                              <div className="share-fill" style={{ width: `${pct}%` }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="loading-text">No Genesis NFTs found in this wallet.</p>
            )}
          </>
        ) : (
          <p className="loading-text">Connect your wallet to view your portfolio.</p>
        )}
      </div>
    </div>
  );
};

export default Account;
